import { Injectable, Injector } from '@angular/core';
import { TrackProductService } from '@analytics/events';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map, mergeMap, tap } from 'rxjs/operators';
import { mapOfferToCommerce } from '@innogy/become-a-customer/shared';

import { choosePropositionAction } from '../bac/10.proposition';
import { getActiveOffers } from '../bac/offers';
import { trackProductAction, trackProductChangeAction } from './offer.actions';

@Injectable()
export class OfferTrackingEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store$: Store<any>,
    private readonly injector: Injector
  ) {}

  activeOffers$ = this.store$.select(getActiveOffers);

  /**
   * Track product-click for selected product.
   */
  public readonly trackProductClick$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(choosePropositionAction),
      map((action) =>
        trackProductAction({
          eventType: 'product-click',
          products: [action.offer],
        })
      )
    );
  });

  /**
   *  Track product-impression for active product(s).
   */
  public readonly trackProductImpression$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(trackProductChangeAction),
      concatLatestFrom(() => [this.activeOffers$]),
      mergeMap(([_, activeOffers]) => {
        return [
          trackProductAction({
            eventType: 'product-impression',
            products: activeOffers,
          }),
        ];
      })
    );
  });

  /**
   *  Calls tracking service to track product events.
   */
  public readonly trackProduct$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(trackProductAction),
        tap(({ products, eventType }) => {
          /**
           * WTA-17: resolve injector context
           */
          const trackProductService = this.injector.get(TrackProductService);

          const mappedProducts = products.map((p, index) =>
            mapOfferToCommerce(p, {
              index,
              listName: '',
              numberOfProducts: 1,
            })
          );
          mappedProducts.forEach((commerce) => {
            if (eventType === 'product-click') {
              trackProductService.trackSelection(commerce);
            }

            if (eventType === 'product-impression') {
              trackProductService.trackImpression(commerce);
            }

            if (eventType === 'product-detail') {
              trackProductService.trackDetail(commerce);
            }
          });
        })
      );
    },
    { dispatch: false }
  );
}
